import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";

/**
 * Global capture point for API failures.
 *
 * The QueryClient is constructed in index.js outside the React tree, so its
 * cache callbacks cannot call a hook. They publish onto this module-level bus
 * instead, and the provider subscribes once it mounts.
 */

const listeners = new Set();

// Failures can fire before the provider has mounted (a 401 while LoginGuard is
// still gating, for instance). Hold the first one so it is not lost.
let pendingError = null;

/**
 * Publishes an error to the modal. Called by the react-query cache handlers.
 *
 * @param {Error} error - The rejected error.
 * @param {object} meta - The query/mutation `meta` object, if any.
 */
export function reportApiError(error, meta) {
  if (meta?.suppressErrorModal) return;

  if (listeners.size === 0) {
    pendingError = pendingError ?? error ?? new Error("Unknown error");
    return;
  }

  listeners.forEach((listener) => listener(error));
}

const ErrorModalContext = createContext(null);

// A sustained outage refetches on every window focus, which would otherwise
// reopen the same modal the moment the user dismisses it. Only an identical
// failure is muted, so a different action failing still gets through.
const REPEAT_SUPPRESSION_MS = 5000;

function signatureOf(error) {
  return `${error?.status ?? "none"}|${error?.message ?? ""}`;
}

export function ErrorModalProvider({ children }) {
  const [error, setError] = useState(null);
  const currentRef = useRef(null);
  const dismissedRef = useRef({ signature: null, at: 0 });

  // Only the first error is kept. Several queries failing together usually
  // share one root cause, and queueing them would make the user dismiss the
  // same outage three times.
  const showError = useCallback((nextError) => {
    if (currentRef.current) return;
    currentRef.current = nextError || new Error("Unknown error");
    setError(currentRef.current);
  }, []);

  const reportToProvider = useCallback(
    (nextError) => {
      const dismissed = dismissedRef.current;
      if (
        dismissed.signature === signatureOf(nextError) &&
        Date.now() - dismissed.at < REPEAT_SUPPRESSION_MS
      ) {
        return;
      }
      showError(nextError);
    },
    [showError]
  );

  const clearError = useCallback(() => {
    dismissedRef.current = {
      signature: signatureOf(currentRef.current),
      at: Date.now(),
    };
    currentRef.current = null;
    setError(null);
  }, []);

  useEffect(() => {
    listeners.add(reportToProvider);

    if (pendingError) {
      const drained = pendingError;
      pendingError = null;
      reportToProvider(drained);
    }

    return () => {
      listeners.delete(reportToProvider);
    };
  }, [reportToProvider]);

  const value = useMemo(
    () => ({ error, showError, clearError }),
    [error, showError, clearError]
  );

  return (
    <ErrorModalContext.Provider value={value}>
      {children}
    </ErrorModalContext.Provider>
  );
}

ErrorModalProvider.propTypes = {
  children: PropTypes.node,
};

/**
 * Returns the error modal context, or null when no provider is above. The
 * modal is mounted app-wide, so a missing provider must degrade to a no-op
 * rather than take down the whole tree.
 */
export function useErrorModal() {
  return useContext(ErrorModalContext);
}
